import React from 'react';
import './BuildPanel.css';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import BuildSlot from './BuildSlot';
import { CHARACTER_SEX, SLOT_IDX } from '../../core/Build';
import Toggle from '../utils/Toggle';

const TXT_CHARACTER_SEX = { fr: 'Sexe du personnage', en: 'Character sex', de: 'Geschlecht des Charakters', es: 'Sexo del personaje', pt: 'Sexo do personagem' };
const TXT_MALE = { fr: 'Homme', en: 'Male', de: 'Männlich', es: 'Hombre', pt: 'Homem' };
const TXT_FEMALE = { fr: 'Femme', en: 'Female', de: 'Weiblich', es: 'Mujer', pt: 'Mulher' };
const BuildPanel = ({
    build,
    setBuild,
    onTooltipShow,
    onTooltipHide,
    genConfig,
    setGenConfig,
    isGenerating = false,
    isServerUpdate = false,
    lang = 'fr',
    buildTopBadge = null,
    canEditBuild = true,
    canEditGenerator = true,
}) => {
    const handleMouseEnter = (item, options) => (e) => {
        if (onTooltipShow) {
            onTooltipShow(item, e, options);
        }
    };

    // The event carries relatedTarget, which is how the hook tells "the pointer
    // moved into the tooltip" from "the pointer left".
    const handleMouseLeave = (e) => {
        if (onTooltipHide) {
            onTooltipHide(e);
        }
    };

    const handleRemoveItem = (slotId) => {
        if (!canEditBuild) return;
        setBuild(build.removeItem(slotId));
        // Also unlock the slot when an item is removed
        if (genConfig.lockedSlots[slotId]) {
            setGenConfig(genConfig.toggleLockedSlot(slotId));
        }
    };

    const handleLockSlot = (slotId, item) => {
        if (!canEditBuild) return;
        setGenConfig(genConfig.toggleLockedSlot(slotId, item));
    };

    if (!build) return null;

    const renderSlot = (item, slotId, name) => (
        <BuildSlot
            item={item}
            slotId={slotId}
            name={name}
            lang={lang}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onRemove={() => handleRemoveItem(slotId)}
            onLock={() => handleLockSlot(slotId, item)}
            setGenConfig={setGenConfig}
            isLocked={!!genConfig.lockedSlots[slotId]}
            build={build}
            setBuild={setBuild}
            isGenerating={isGenerating}
            isServerUpdate={isServerUpdate}
            canEditBuild={canEditBuild}
            canEditGenerator={canEditGenerator}
        />
    );

    const renderTrophiesGrid = () => (
        <div className="trophies-grid">
            {build.getTrophus().map((item, index) => {
                const slotId = SLOT_IDX.TROPHUS[index];
                return (
                    <BuildSlot
                        key={`Trophus${index}`}
                        item={item}
                        slotId={slotId}
                        name={`Trophus${index}`}
                        lang={lang}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        onRemove={() => handleRemoveItem(slotId)}
                        onLock={() => handleLockSlot(slotId, item)}
                        setGenConfig={setGenConfig}
                        isLocked={!!genConfig.lockedSlots[slotId]}
                        build={build}
                        setBuild={setBuild}
                        isGenerating={isGenerating}
                        isServerUpdate={isServerUpdate}
                        canEditBuild={canEditBuild}
                        canEditGenerator={canEditGenerator}
                    />
                );
            })}
        </div>
    );

    const characterSex = build.getCharacterSex?.() ?? build.characterSex ?? CHARACTER_SEX.MALE;
    const handleCharacterSexChange = (nextSex) => {
        if (!canEditBuild || characterSex === nextSex || typeof build.setCharacterSex !== 'function') return;
        setBuild(build.setCharacterSex(nextSex));
    };
    const characterSexToggle = (
        <Toggle
            className="build-sex-toggle"
            variant="pill"
            ariaLabel={TXT_CHARACTER_SEX[lang] || TXT_CHARACTER_SEX.en}
            value={characterSex}
            onChange={handleCharacterSexChange}
            disabled={!canEditBuild}
            items={[
                {
                    value: CHARACTER_SEX.MALE,
                    icon: <MaleIcon />,
                    ariaLabel: TXT_MALE[lang] || TXT_MALE.en,
                },
                {
                    value: CHARACTER_SEX.FEMALE,
                    icon: <FemaleIcon />,
                    ariaLabel: TXT_FEMALE[lang] || TXT_FEMALE.en,
                },
            ]}
        />
    );

    const hasCenterColumn = !!characterSexToggle;
    const slotsGridClass = hasCenterColumn ? 'build-slots build-slots--with-center' : 'build-slots';

    return (
        <div className="build-grid">
            <div className={slotsGridClass}>
                <div className='build-column left'>
                    {renderSlot(build.getAmulet(), SLOT_IDX.AMULET, 'Amulet')}
                    {renderSlot(build.getShield(), SLOT_IDX.SHIELD, 'Shield')}
                    {renderSlot(build.getRingL(), SLOT_IDX.RING1, 'RingL')}
                    {renderSlot(build.getBelt(), SLOT_IDX.BELT, 'Belt')}
                    {renderSlot(build.getBoots(), SLOT_IDX.BOOTS, 'Boots')}
                </div>
                {hasCenterColumn && (
                    <div className="build-slots-center">
                        {buildTopBadge ? (
                            <div className="build-slots-menu">{buildTopBadge}</div>
                        ) : null}
                        {characterSexToggle}
                    </div>
                )}
                <div className='build-column right'>
                    {renderSlot(build.getHat(), SLOT_IDX.HAT, 'Hat')}
                    {renderSlot(build.getWeapon(), SLOT_IDX.WEAPON, 'Weapon')}
                    {renderSlot(build.getRingR(), SLOT_IDX.RING2, 'RingR')}
                    {renderSlot(build.getCloak(), SLOT_IDX.CLOAK, 'Cloak')}
                    {renderSlot(build.getPet(), SLOT_IDX.PET, 'Pet')}
                </div>
            </div>
            <div className="build-trophies-row">
                {renderTrophiesGrid()}
            </div>
        </div>
    );
};

export default BuildPanel; 
